import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useLazyQuery, useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";
import { zodResolver } from "@hookform/resolvers/zod";

import { Input, FormGroup } from "@cofano/cck";
import { Alert } from "@mui/material";

import { FormDialog } from "components/dialogs/Dialogs";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language } from "imports/Utils";

import RelationSelect from "view/relations/components/selects/RelationSelect";

import { ShipFromQuery } from "view/ships/ShipDetails";
import { updateShipNameSchema, UpdateShipNameSchemaInput, UpdateShipNameSchemaOutput } from "view/ships/utils/ship.schema";

import { RelationType } from "__generated__/enums";
import { ReviseShipDocument } from "__generated__/view/ships/graphql/ship.mutations.generated";
import { GetShipForFormDocument } from "__generated__/view/ships/graphql/ship.queries.generated";

interface UpdateShipNameDialogProps {
	open: boolean;
	onClose: () => void;
	ship: ShipFromQuery;
}

const UpdateShipNameDialog = ({ open, onClose, ship }: UpdateShipNameDialogProps) => {

	const navigate = useNavigate();

	const [ loading, setLoading ] = useState(false);
	const [ getShip ] = useLazyQuery(GetShipForFormDocument);

	const defaultValues = {
		name: ship.name,
		operator: ship.operator ?? null,
	};

	const { control, handleSubmit, reset } = useForm<UpdateShipNameSchemaInput, unknown, UpdateShipNameSchemaOutput>({
		resolver: zodResolver(updateShipNameSchema),
		defaultValues: defaultValues,
	});

	const handleOnClose = () => {
		onClose();
		reset(defaultValues);
	};

	const [ revise ] = useMutation(ReviseShipDocument);

	const save = (data: UpdateShipNameSchemaOutput) => {
		setLoading(true);

		getShip({
			variables: {
				id: ship.id,
			},
		}).then(response => {

			const ship = {
				...response.data?.ship,
				skipper: undefined,
				tankProperties: response.data?.ship.tankProperties ? {
					tankConfiguration: response.data.ship.tankProperties.tankConfiguration,
				} : undefined,
				cemtClassificationType: response.data?.ship.cemtClassificationType ? {
					id: response.data.ship.cemtClassificationType.id,
				} : undefined,
				shipOwner: undefined,
				tanks: response.data?.ship.tanks?.map(tank => ({ ...tank, id: null })),
				sailingSchedule: response.data?.ship.sailingSchedule?.id,
			};

			revise({
				variables: {
					operatorId: data.operator?.id,
					name: data.name,
					input: ship,
				},
			}).then(response => {
				handleOnClose();
				navigate(`/view/ships/${response.data?.reviseShip.id}/details`);
			}).catch(() => {
				setLoading(false);
			});
		}).catch(() => {
			setLoading(false);
		});
	};

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "EDIT_NAME" })}
			form='updateShipNameForm'
			loading={loading}>
			<form onSubmit={(event) => void handleSubmit(save)(event)} id="updateShipNameForm">
				{ship.nextCall && (
					<Alert severity="warning" style={{ marginBottom: 16 }}>
						{Language("SHIP_HAS_UNFINISHED_VOYAGE_WARNING")}
					</Alert>
				)}

				<FormGroup label={intl.formatMessage({ id: "SHIP_ADD_NAME" })} overflow="hidden" required>
					<Controller
						name='name'
						control={control}
						render={({ field, formState }) => (
							<React.Fragment>
								<Input {...field} autoFocus />
								<FormErrorMessage name={field.name} errors={formState.errors} />
							</React.Fragment>
						)} />
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "OPERATOR" })} overflow="hidden">
					<Controller
						name="operator"
						control={control}
						render={({ field }) => <RelationSelect value={field.value} onChange={field.onChange} types={[ RelationType.Operator ]} />} />
				</FormGroup>
			</form>
		</FormDialog>
	);
};

export default UpdateShipNameDialog;
